import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, Undo2, PauseCircle, XCircle } from 'lucide-react';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import { inventoryApi, type DamageRow } from '../../services/inventoryApi';

/**
 * DAMAGE WIZARD — mockup screen C. Reached from any unit row's ⚠ button or
 * the Damage tab's "Log damage" button; always answers WHO PAYS and what
 * happens to the machine. Three steps, per the F1 brief:
 *   1. What happened (required)
 *   2. Outcome — RMA / Hold / Write-off (drives `state` + a sane `chargedTo` default)
 *   3. Confirm — remarks, charged-to override, amount, submit
 *
 * Wires to the EXISTING `POST /damage` (`inventoryApi.logDamage`) — no new
 * endpoint. `state` (rma / on_hold / retired) is accepted by that call per
 * b1-lifecycle-report.md's "damage allow-list" note (already a member of
 * `DAMAGE_ALLOWED_STATES`, unchanged by this wave).
 */

type Outcome = 'rma' | 'on_hold' | 'retired';

const OUTCOMES: { id: Outcome; title: string; desc: string; chargedTo: DamageRow['chargedTo']; icon: React.FC<any> }[] = [
  {
    id: 'rma',
    title: '↩ Return to vendor (RMA)',
    desc: 'Ships back to the vendor under its GRN. Unit → rma, damage charged to vendor (credit expected). Vendor defect % updates.',
    chargedTo: 'vendor',
    icon: Undo2,
  },
  {
    id: 'on_hold',
    title: '⏸ Hold with remarks',
    desc: 'Keep the machine, record what happened and why it’s held. Unit → on hold; charged-to stays open until resolved.',
    chargedTo: 'tenant',
    icon: PauseCircle,
  },
  {
    id: 'retired',
    title: '✕ Write off',
    desc: 'Beyond repair. Unit → retired, value charged to write-off (or tenant per agreement damage clause). Feeds the damage & loss report.',
    chargedTo: 'writeoff',
    icon: XCircle,
  },
];

const OUTCOME_CTA: Record<Outcome, string> = {
  rma: 'Record damage → RMA',
  on_hold: 'Record damage → hold',
  retired: 'Record damage → write-off',
};

interface Props {
  /** Prefilled when opened from a unit row's ⚠ button. Absent from the Damage tab's generic "Log damage" — step 1 then asks for it. */
  unitId?: string;
  /** Prefills the amount field, e.g. from `unit.costCents` — never fabricated when unknown. */
  unitCostCents?: number;
  /** 'receiving' | 'in_stock' | 'deployed' | 'other' — where in its life this happened. */
  defaultStage?: string;
  onClose: () => void;
  onSaved: () => void;
}

const DamageWizard: React.FC<Props> = ({ unitId: unitIdProp, unitCostCents, defaultStage = 'in_stock', onClose, onSaved }) => {
  const [step, setStep] = useState(1);
  const [unitIdInput, setUnitIdInput] = useState('');
  const [reason, setReason] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [remarks, setRemarks] = useState('');
  const [chargedTo, setChargedTo] = useState<DamageRow['chargedTo']>('vendor');
  const [amount, setAmount] = useState(typeof unitCostCents === 'number' ? (unitCostCents / 100).toFixed(2) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unitId = unitIdProp || unitIdInput.trim();

  const pickOutcome = (o: Outcome) => {
    setOutcome(o);
    setChargedTo(OUTCOMES.find((x) => x.id === o)!.chargedTo);
  };

  const canNext1 = reason.trim().length > 0 && unitId.length > 0;
  const canNext2 = !!outcome;

  const submit = async () => {
    if (!outcome || !unitId) return;
    setSaving(true);
    setError(null);
    try {
      await inventoryApi.logDamage({
        unitId,
        stage: defaultStage,
        reason: reason.trim(),
        chargedTo,
        amountCents: amount ? Math.round(Number(amount) * 100) : undefined,
        notes: remarks.trim() || undefined,
        state: outcome,
      });
      toastService.success(
        `Damage logged — ${unitId} → ${outcome === 'rma' ? 'RMA' : outcome === 'on_hold' ? 'on hold' : 'retired'}. ` +
        'Report is OPEN — resolve or cancel it from the Damage tab, or this unit\'s page, once it\'s settled.',
      );
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to log damage');
    } finally {
      setSaving(false);
    }
  };

  const stepLabel = useMemo(() => `Step ${step} of 3 · ${
    step === 1 ? 'what happened?' : step === 2 ? 'what happens to this machine?' : 'confirm & record'
  }`, [step]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white text-gray-900 shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="bg-black px-5 py-4 text-white shrink-0">
          <div className="text-[11px] text-gray-500">Inventory › Damage{unitId ? ` › ${unitId}` : ''}</div>
          <h2 className="text-base font-bold">Report damage{unitId ? ` — ${unitId}` : ''}</h2>
          <div className="text-[11px] text-gray-400 mt-0.5">{stepLabel}</div>
        </div>
        <div style={{ background: 'linear-gradient(90deg,#e8365a,#DC143C,#9F1239)', height: 3 }} />

        <div className="p-5 overflow-y-auto space-y-4">
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              {!unitIdProp && (
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">
                    Unit ID (required)
                  </label>
                  <input
                    autoFocus
                    value={unitIdInput}
                    onChange={(e) => setUnitIdInput(e.target.value)}
                    placeholder="RTA-000107"
                    className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm font-mono min-h-[44px]"
                  />
                </div>
              )}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">
                  What happened? (required)
                </label>
                <textarea
                  autoFocus={!!unitIdProp}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  placeholder="e.g. Cracked screen — arrived damaged in shipping carton"
                  className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm resize-none"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {OUTCOMES.map((o) => {
                const on = outcome === o.id;
                const Icon = o.icon;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => pickOutcome(o.id)}
                    className={`text-left rounded-xl border p-4 transition ${on ? 'shadow-[0_0_0_1px_#DC143C]' : 'hover:border-stone-300'}`}
                    style={on ? { borderColor: '#DC143C', background: '#fef2f4' } : { borderColor: '#e7e5e4', background: '#fff' }}
                  >
                    <div className="flex items-center gap-2 text-[12.5px] font-bold mb-1.5">
                      <Icon size={15} /> {o.title}
                    </div>
                    <div className="text-[10.5px] text-stone-500 leading-relaxed">{o.desc}</div>
                  </button>
                );
              })}
            </div>
          )}

          {step === 3 && outcome && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">
                  <strong className="text-stone-800">{OUTCOMES.find((o) => o.id === outcome)!.title}</strong>
                  <p className="mt-1">{reason}</p>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">
                    Remarks / next action
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    placeholder="e.g. RMA label requested, awaiting pickup"
                    className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm resize-none"
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Charged to</label>
                  <SearchableDropdown
                    options={[
                      { value: 'vendor', label: 'Vendor — credit requested' },
                      { value: 'tenant', label: 'Tenant — per agreement' },
                      { value: 'writeoff', label: 'Write-off' },
                    ]}
                    value={chargedTo}
                    onChange={(v) => setChargedTo((v as any) || 'writeoff')}
                    variant="light"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">
                    Amount {typeof unitCostCents === 'number' ? '(unit cost, prefilled)' : '($, optional)'}
                  </label>
                  <input
                    type="number" min={0} step={0.01}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm min-h-[44px]"
                  />
                </div>
                <button
                  onClick={submit}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50 min-h-[44px]"
                  style={{ background: '#DC143C' }}
                >
                  {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : null}
                  {OUTCOME_CTA[outcome]}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-stone-200 px-5 py-4 shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-300 text-sm font-medium text-stone-700">
            Cancel
          </button>
          <div className="flex gap-2">
            {step > 1 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-stone-300 text-sm font-medium text-stone-700"
              >
                <ArrowLeft size={14} /> Back
              </button>
            )}
            {step < 3 && (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-40"
                style={{ background: '#DC143C' }}
              >
                Next <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DamageWizard;
